import dynamic from "next/dynamic"
import Image from "next/image"
import { ShortsVideo } from "types/event"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa"
import { useState, useEffect } from "react"
import Modal from "components/Modal/Modal"

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false })

interface Props {
    video: ShortsVideo
    onClose: () => void
    onNext: () => void
    onPrev: () => void
}

export default function VideoModal({ video, onClose, onNext, onPrev }: Props) {
    const [isMuted, setIsMuted] = useState(false) // start muted
    const [playerKey, setPlayerKey] = useState(video._id) // force reload

    const [isLaoding, setIsLoading] = useState(true)
    // When video changes, reset player key and keep it muted for autoplay
    useEffect(() => {
        setPlayerKey(video._id)
    }, [video._id])

    const stopPropagation = (e: React.MouseEvent) => e.stopPropagation()

    return (
        <Modal isOpen={true}>
            <div className="fixed inset-0 flex justify-center items-center bg-black/50 z-50">
                {/* Prev Button */}
                <button
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full z-50"
                    onClick={(e) => {
                        e.stopPropagation()
                        onPrev()
                    }}
                >
                    <FaChevronLeft size={20} />
                </button>

                {/* Video Container */}
                <div
                    className="relative w-full max-w-sm aspect-[9/16] bg-black rounded-[20px] overflow-hidden shadow-lg"
                    onClick={stopPropagation}
                >
                    <ReactPlayer
                        key={playerKey}
                        src={video.url}
                        playing={true} // always play
                        preload="auto"
                        playsInline
                        muted={isMuted} // start muted to allow autoplay
                        loop
                        width="100%"
                        height="100%"
                        onReady={() => setIsLoading(false)}
                        className="object-cover"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                    {/* Close */}
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 text-2xl text-white z-50"
                    >
                        ×
                    </button>

                    {/* Performer */}
                    <div className="absolute top-3 left-3 z-50 text-white text-lg font-semibold">
                        {video.performer}
                    </div>

                    {/* Mute Toggle */}
                    <div
                        className="absolute bottom-3 left-3 z-50 cursor-pointer"
                        onClick={() => setIsMuted((prev) => !prev)}
                    >
                        {isMuted ? (
                            <Image
                                src="/svg/v2/muted.svg"
                                width={32}
                                height={32}
                                className="w-8 h-8"
                                alt="muted"
                            />
                        ) : (
                            <Image
                                src="/svg/v2/sound.svg"
                                width={32}
                                height={32}
                                className="w-8 h-8"
                                alt="sound"
                            />
                        )}
                    </div>
                </div>

                {/* Next Button */}
                <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 text-white rounded-full z-50"
                    onClick={(e) => {
                        e.stopPropagation()
                        onNext()
                    }}
                >
                    <FaChevronRight size={20} />
                </button>
            </div>
        </Modal>
    )
}
